import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import {
  EmployeeData,
  EmployeeCreateData,
  EmployeeUpdateData,
  FetchEmployeesParams,
} from '../types/employeeTypes';
import { uploadFileToS3 } from '../../utils/s3Upload';
import { fetchUsers } from './userActions';

// Utility function to ensure consistent response handling
const handleApiResponse = (response: any) => {
  if (response.data?.success === false) {
    throw new Error(response.data?.message || 'API request failed');
  }
  
  // The response is already properly formatted with signed URLs by the backend
  // No additional processing needed here since the backend now handles URL signing
  return response.data;
};

// Fetch all employees with pagination
export const fetchEmployees = createAsyncThunk(
  'employees/fetchEmployees',
  async (params: FetchEmployeesParams = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search } = params;
      const response = await axiosInstance.get('/v1/admin/employee', {
        params: { page, limit, ...(search ? { search } : {}) },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch employees'
      );
    }
  }
);

// Fetch all employees without pagination (for dropdowns, etc.)
export const getAllEmployees = createAsyncThunk(
  'employees/getAllEmployees',
  async (_, { rejectWithValue }) => {
    try {
      // Set a high limit to get all employees in one request
      // And include permissions data
      const response = await axiosInstance.get('/v1/admin/employee', {
        params: { 
          limit: 1000,
          with: 'permissions' 
        },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch all employees'
      );
    }
  }
);

// Fetch only employees that don't have "Employee" designation
export const fetchNonEmployeeStaff = createAsyncThunk(
  'employees/fetchNonEmployeeStaff',
  async (_, { rejectWithValue, getState }) => {
    try {
      // First fetch all employees
      const response = await axiosInstance.get('/v1/admin/employee', {
        params: { limit: 100 }, // Using higher limit to ensure we get most staff
      });

      const apiResponse = handleApiResponse(response);

      // Filter out employees with "Employee" designation
      if (apiResponse && apiResponse.data) {
        const filteredEmployees = apiResponse.data.filter(
          (employee: EmployeeData) =>
            employee.designation && employee.designation.name !== 'Employee'
        );

        return {
          ...apiResponse,
          data: filteredEmployees,
        };
      }

      return apiResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch non-employee staff'
      );
    }
  }
);

// Fetch only employees that have applicationAccess enabled (for Users page)
export const fetchUsersWithAccess = createAsyncThunk(
  'employees/fetchUsersWithAccess',
  async (_, { rejectWithValue }) => {
    try {
      // First fetch all employees
      const response = await axiosInstance.get('/v1/admin/employee', {
        params: { limit: 100 }, // Using higher limit to ensure we get all employees
      });

      const apiResponse = handleApiResponse(response);

      // Filter only employees with applicationAccess enabled
      if (apiResponse && apiResponse.data) {
        const filteredEmployees = apiResponse.data.filter(
          (employee: EmployeeData) => employee.applicationAccess === true
        );

        return {
          ...apiResponse,
          data: filteredEmployees,
        };
      }

      return apiResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch users with access'
      );
    }
  }
);

// Fetch a single employee by ID
export const fetchEmployeeById = createAsyncThunk(
  'employees/fetchEmployeeById',
  async (employeeId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/admin/employee/${employeeId}`);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch employee'
      );
    }
  }
);

// Create a new employee
export const createEmployee = createAsyncThunk(
  'employees/createEmployee',
  async (employeeData: EmployeeCreateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('/v1/admin/employee/create', employeeData);
      
      // After successfully creating an employee, refresh the users data in redux
      // This ensures the new employee appears in dropdowns without requiring logout/login
      await dispatch(fetchUsers({ forceRefresh: true }) as any);
      
      return handleApiResponse(response);
    } catch (error: any) {
      // Return the complete error object from the API response
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Failed to create employee' });
    }
  }
);

// Update an existing employee
export const updateEmployee = createAsyncThunk(
  'employees/updateEmployee',
  async ({ id, data }: { id: number; data: EmployeeUpdateData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/v1/admin/employee/${id}`, data);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update employee'
      );
    }
  }
);

// Search employees
export const searchEmployees = createAsyncThunk(
  'employees/searchEmployees',
  async (query: string, { rejectWithValue }) => {
    try {
      // This makes a direct API call with the search parameter
      // Server-side will handle the filtering
      const response = await axiosInstance.get('/v1/admin/employee', {
        params: { search: query },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to search employees'
      );
    }
  }
);

// Fetch only employees that have management permission
export const fetchEmployeesWithManagementPermission = createAsyncThunk(
  'employees/fetchEmployeesWithManagementPermission',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/v1/admin/manager-employees');
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch employees with management permission'
      );
    }
  }
);

// Upload employee photo to S3 and get file URL
export const uploadEmployeePhoto = createAsyncThunk(
  'employees/uploadEmployeePhoto',
  async (file: File, { rejectWithValue }) => {
    try {
      console.log('Starting employee photo upload for file:', file.name);
      // Use the existing S3 upload utility, but specify the employee-photos folder
      const fileUrl = await uploadFileToS3(file, 'employee-photos');
      console.log('Employee photo upload successful, URL:', fileUrl);
      
      // Verify the URL is valid
      if (!fileUrl || typeof fileUrl !== 'string' || !fileUrl.startsWith('http')) {
        throw new Error('Invalid URL returned from S3 upload');
      }
      
      return fileUrl;
    } catch (error: any) {
      console.error('Employee photo upload failed:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to upload employee photo'
      );
    }
  }
);
